import { useState, ViewTransition } from "react";
import { useNavigate } from "react-router";
import * as stylex from "@stylexjs/stylex";

import { BLOG_POSTS, CATEGORIES } from "../data/placeholder";

function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const filteredPosts =
    selectedCategory === "All"
      ? BLOG_POSTS
      : BLOG_POSTS.filter((post) => post.category === selectedCategory);

  const handlePostClick = (post) => {
    navigate(`/blog/${post.id}`);
  };

  return (
    <div {...stylex.props(styles.page)}>
      <div {...stylex.props(styles.container)}>
        <ViewTransition>
          <div {...stylex.props(styles.header)}>
            <h1 {...stylex.props(styles.title, styles.titleGradient)}>
              Thoughts & Ideas
            </h1>
            <p {...stylex.props(styles.subtitle)}>
              Exploring the art and science of building exceptional digital
              experiences.
            </p>
          </div>
        </ViewTransition>

        <ViewTransition>
          <div {...stylex.props(styles.categoryFilter)}>
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                {...stylex.props(
                  styles.categoryBtn,
                  selectedCategory === category && styles.categoryBtnActive,
                )}
              >
                {category}
              </button>
            ))}
          </div>
        </ViewTransition>

        <ViewTransition>
          <div {...stylex.props(styles.blogList)}>
            {filteredPosts.map((post) => (
              <ViewTransition key={post.id} name={`post-list-${post.id}`}>
                <article
                  {...stylex.props(
                    styles.blogRow,
                    hoveredCard === post.id && styles.blogRowHovered,
                  )}
                  onClick={() => handlePostClick(post)}
                  onMouseEnter={() => setHoveredCard(post.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div {...stylex.props(styles.rowImage)}>
                    <img
                      src={post.image}
                      alt={post.title}
                      {...stylex.props(styles.image)}
                    />
                  </div>
                  <div {...stylex.props(styles.rowContent)}>
                    <div {...stylex.props(styles.rowMeta)}>
                      <span {...stylex.props(styles.categoryTag)}>
                        {post.category}
                      </span>
                      <span {...stylex.props(styles.metaText)}>
                        {post.date}
                      </span>
                      <span {...stylex.props(styles.metaDot)}>•</span>
                      <span {...stylex.props(styles.metaText)}>
                        {post.readTime}
                      </span>
                    </div>
                    <h2 {...stylex.props(styles.rowTitle)}>{post.title}</h2>
                    <p {...stylex.props(styles.rowExcerpt)}>{post.excerpt}</p>
                  </div>
                </article>
              </ViewTransition>
            ))}
          </div>
        </ViewTransition>
      </div>
    </div>
  );
}

export default Blog;

const fadeIn = stylex.keyframes({
  "0%": { opacity: 0, transform: "translateY(20px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const styles = stylex.create({
  blogList: {
    gap: "1rem",
    animationDelay: "0.2s",
    animationDuration: "0.6s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    display: "flex",
    flexDirection: "column",
  },
  blogRow: {
    padding: "1rem",
    borderColor: "var(--media-inner-border)",
    borderRadius: "var(--card-corner-radius)",
    borderStyle: "solid",
    borderWidth: "1px",
    gap: {
      default: "1.5rem",
      "@media (max-width: 768px)": "1rem",
    },
    overflow: "hidden",
    transition: "all 0.3s ease",
    alignItems: "center",
    backdropFilter: "blur(20px)",
    backgroundColor: "var(--surface-glass)",
    boxShadow: "var(--shadow-sm)",
    cursor: "pointer",
    display: "flex",
  },
  blogRowHovered: {
    borderColor: "var(--glow-border)",
    boxShadow: "var(--shadow-neon)",
    transform: "translateY(-2px)",
  },
  categoryBtn: {
    borderColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--primary-accent)",
    },
    borderRadius: "50px",
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "all 0.2s ease",
    backgroundColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--primary-accent)",
    },
    boxShadow: {
      default: null,
      ":hover": "0 4px 12px rgba(0,0,0,0.1)",
    },
    color: {
      default: "var(--primary-text)",
      ":hover": "#FFFFFF",
    },
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
    paddingBottom: "0.5rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
  },
  categoryBtnActive: {
    borderColor: "var(--primary-accent)",
    backgroundColor: "var(--primary-accent)",
    boxShadow: "var(--shadow-neon)",
    color: "#FFFFFF",
  },
  categoryFilter: {
    gap: "0.75rem",
    animationDelay: "0.1s",
    animationDuration: "0.6s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    marginBottom: "1rem",
  },
  categoryTag: {
    borderColor: "var(--primary-accent)",
    borderRadius: "50px",
    borderStyle: "solid",
    borderWidth: "1px",
    backgroundColor: "rgba(121, 40, 202, 0.1)",
    color: "var(--primary-accent)",
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    paddingBottom: "0.2rem",
    paddingLeft: "0.6rem",
    paddingRight: "0.6rem",
    paddingTop: "0.2rem",
  },
  container: {
    gap: "2rem",
    marginInline: "auto",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    maxWidth: "900px",
    paddingBottom: "4rem",
    paddingLeft: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingRight: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingTop: "4rem",
    width: "100%",
  },
  header: {
    animationDuration: "0.8s",
    animationFillMode: "both",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    textAlign: "center",
    marginBottom: "1rem",
  },
  image: {
    transition: "transform 0.3s ease",
    objectFit: "cover",
    height: "100%",
    width: "100%",
  },
  metaDot: {
    color: "var(--text-secondary)",
    opacity: 0.5,
  },
  metaText: {
    color: "var(--text-secondary)",
    fontSize: "0.8rem",
  },
  page: {
    backgroundAttachment: "fixed",
    backgroundImage: "var(--bg-gradient)",
    minHeight: "100vh",
    width: "100%",
  },
  rowContent: {
    gap: "0.5rem",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minWidth: 0,
  },
  rowExcerpt: {
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    color: "var(--text-secondary)",
    display: {
      default: "-webkit-box",
      "@media (max-width: 600px)": "none",
    },
    fontSize: "0.9rem",
    lineHeight: 1.5,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  rowImage: {
    borderRadius: "12px",
    overflow: "hidden",
    flexShrink: 0,
    height: {
      default: "100px",
      "@media (max-width: 768px)": "80px",
    },
    width: {
      default: "150px",
      "@media (max-width: 768px)": "100px",
    },
  },
  rowMeta: {
    gap: "0.5rem",
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
  },
  rowTitle: {
    overflow: "hidden",
    color: "var(--primary-text)",
    fontSize: {
      default: "1.1rem",
      "@media (max-width: 768px)": "1rem",
    },
    fontWeight: 700,
    lineHeight: 1.3,
    textOverflow: "ellipsis",
    whiteSpace: {
      default: "nowrap",
      "@media (max-width: 768px)": "normal",
    },
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  subtitle: {
    marginInline: "auto",
    color: "var(--text-secondary)",
    fontSize: "1.1rem",
    lineHeight: 1.6,
    marginBottom: 0,
    marginTop: 0,
    maxWidth: "500px",
  },
  title: {
    color: "var(--primary-text)",
    display: "block",
    fontSize: {
      default: "3.5rem",
      "@media (max-width: 768px)": "2.25rem",
    },
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.3,
    position: "relative",
    marginBottom: "1rem",
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  titleGradient: {
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundImage: "var(--primary-gradient)",
  },
});
